import { HomeFilled, MessageFilled, MoreOutlined, SearchOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { NavLink } from 'react-router-dom';

import { FONT_WEIGHT, TOKEN } from '~/configs/theme.ts';
import useMatching from '~/features/matching/hooks/useMatching.ts';

/**
 * 원본 앱(Doddies 시안)의 4탭 구조. 커머스형 `홈·결제내역·마이` 에서 매칭형으로 재편했다.
 * @description 결제내역은 탭에서 내리고 설정 하위로 옮긴다 — 매칭 서비스에서 결제는 부가 흐름이라
 *  1급 내비게이션을 차지하면 채팅(핵심 기능)이 밀린다.
 */
const TABS = [
  { to: '/', label: '홈', icon: <HomeFilled />, end: true },
  { to: '/search', label: '검색', icon: <SearchOutlined />, end: false },
  { to: '/chat', label: '채팅', icon: <MessageFilled />, end: false },
  { to: '/settings', label: '설정', icon: <MoreOutlined />, end: false },
];

/**
 * 하단 탭 네비게이션 (Pure UI). 소비자 모바일 앱 공통 네비게이션.
 */
function BottomTab() {
  // 안읽음 총합은 탭에서만 쓰는 파생값이라 구독만 한다(fetch 는 각 페이지 책임).
  const { totalUnread } = useMatching();

  return (
    <nav css={styles.nav}>
      {TABS.map(tab => (
        <NavLink
          key={tab.to}
          className={({ isActive }) => (isActive ? 'active' : '')}
          css={styles.tab}
          end={tab.end}
          to={tab.to}
        >
          <span css={styles.icon}>
            {tab.icon}
            {tab.to === '/chat' && totalUnread > 0 && <span css={styles.badge}>{totalUnread}</span>}
          </span>
          <span css={styles.label}>{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

const styles = {
  nav: css`
    position: sticky;
    bottom: 0;
    z-index: 10;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-top: 1px solid ${TOKEN.border};
    background: ${TOKEN.bgSurface};
    padding-bottom: env(safe-area-inset-bottom);
  `,
  tab: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 8px 0 10px;
    color: ${TOKEN.textMuted};
    font-size: 18px;
    text-decoration: none;
    transition: color 0.15s;

    &.active {
      color: ${TOKEN.primary};
    }
  `,
  icon: css`
    position: relative;
    font-size: 20px;
    line-height: 1;
  `,
  // 안읽음 뱃지 — 아이콘 우상단에 겹쳐 탭 그리드 폭에 영향을 주지 않게 절대배치한다.
  badge: css`
    position: absolute;
    top: -4px;
    left: 12px;
    display: grid;
    place-items: center;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    border-radius: 8px;
    background: ${TOKEN.primary};
    color: #fff;
    font-size: 10px;
    font-weight: ${FONT_WEIGHT.SEMI_BOLD};
    line-height: 1;
  `,
  label: css`
    font-size: 11px;
  `,
};

export default BottomTab;
